import React from 'react';
import { formatKpiAdaptiveINR } from '../../../utils/kpiAmountFormat';
import ReportCover from './ReportCover';
import ExecutiveSummaryCards from './ExecutiveSummaryCards';
import PortfolioSnapshotTable from './PortfolioSnapshotTable';
import SectionHeader from './SectionHeader';
import MiniKpiStrip from './MiniKpiStrip';
import ProjectSummaryTable from './ProjectSummaryTable';
import EmptyStateCard from './EmptyStateCard';
import ReportAnnexureSection from './ReportAnnexureSection';
import { CorrespondenceStatusChart, RABillValueChart, ProjectActivityChart } from './SummaryChartRow';
import { fmtInr, fmtInrNoSymbol, formatCountValue, formatMetricLabel, moduleHasNoRecords } from './reportUtils';

const Annex = ReportAnnexureSection;

const SeniorManagementReportContent = ({ data, meta }) => {
  if (!data) return null;

  const s = data.summary || {};
  const corr = data.correspondence || {};
  const ra = data.raBills || {};
  const bg = data.bankGuarantees || {};
  const ms = data.milestones || {};
  const dw = data.drawings || {};
  const dev = data.deviations || {};
  const qaqc = data.qaqc || {};
  const clr = data.clearances || {};
  const scope = data.agreementScope || {};

  const corrEmpty = moduleHasNoRecords(corr.summary, corr.byProject, corr.details);
  const raEmpty = moduleHasNoRecords(ra.summary, ra.byProject, ra.details);
  const bgEmpty = moduleHasNoRecords(bg.summary, bg.byProject, bg.details, [bg.insuranceDetails]);
  const msEmpty = moduleHasNoRecords(ms.summary, ms.byProject, ms.details, [ms.eotDetails]);
  const dwEmpty = moduleHasNoRecords(dw.summary, dw.byProject, dw.details);
  const devEmpty = moduleHasNoRecords(dev.summary, dev.byProject, dev.details);
  const qaqcEmpty = moduleHasNoRecords(qaqc.summary, qaqc.byProject, qaqc.details);
  const clrEmpty = moduleHasNoRecords(clr.summary, clr.byProject, clr.details);
  const scopeEmpty = moduleHasNoRecords(scope.summary, scope.byProject, []);

  const comparisonRows = Object.entries(data.comparison || {}).map(([metric, obj]) => ({
    metric: formatMetricLabel(metric),
    current: obj?.currentPeriod ?? obj?.current ?? '—',
    previous: obj?.previousPeriod ?? obj?.previous ?? '—',
  }));

  return (
    <>
      <ReportCover meta={meta} />

      <div className="sm-exec-dashboard">
        <ExecutiveSummaryCards summary={s} />
        <div className="sm-charts sm-charts--exec">
          <CorrespondenceStatusChart summary={corr.summary} />
          <RABillValueChart summary={ra.summary} formatVal={(v) => formatKpiAdaptiveINR(Number(v))} />
          <ProjectActivityChart rows={corr.byProject} />
        </div>
      </div>

      <PortfolioSnapshotTable projectMaster={data.projectMaster || []} />

      {/* Module sections */}
      <SectionHeader id="sm-corr" slug="Module 1" title="Correspondence" first />
      {corrEmpty ? (
        <EmptyStateCard />
      ) : (
        <>
          <MiniKpiStrip summary={corr.summary} max={6} />
          <ProjectSummaryTable
            meta="Project-wise summary"
            emptyText="No project-wise rows for the selected reporting period."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'periodInward', label: 'Inward (period)', align: 'right', render: (v) => formatCountValue(v) },
              { key: 'periodOutward', label: 'Outward (period)', align: 'right', render: (v) => formatCountValue(v) },
              { key: 'periodEntries', label: 'Entries (period)', align: 'right', render: (v) => formatCountValue(v) },
              { key: 'totalChains', label: 'Subjects', align: 'right' },
              { key: 'totalEntries', label: 'Letter entries', align: 'right' },
              { key: 'open', label: 'Open', align: 'right' },
              { key: 'closed', label: 'Closed', align: 'right' },
              { key: 'critical', label: 'Critical', align: 'right' },
              { key: 'overdue', label: 'Overdue', align: 'right' },
            ]}
            rows={corr.byProject || []}
          />
        </>
      )}

      <SectionHeader id="sm-ra" slug="Module 2" title="RA bills" />
      {raEmpty ? (
        <EmptyStateCard />
      ) : (
        <>
          <MiniKpiStrip
            summary={ra.summary}
            max={8}
            formatValue={(k, v) => {
              const kl = String(k).toLowerCase();
              if (kl.includes('value')) return formatKpiAdaptiveINR(Number(v));
              return formatCountValue(v);
            }}
          />
          <ProjectSummaryTable
            meta="Project-wise summary"
            emptyText="No project-wise rows for the selected reporting period."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'billCount', label: 'Bills', align: 'right' },
              { key: 'gross', label: 'Cumulative gross (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
              { key: 'net', label: 'Net (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
              { key: 'certified', label: 'Certified (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
              { key: 'received', label: 'Received (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
              { key: 'pending', label: 'Pending (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
            ]}
            rows={ra.byProject || []}
          />
        </>
      )}

      <SectionHeader id="sm-bg" slug="Module 3" title="BG / insurance" />
      {bgEmpty ? (
        <EmptyStateCard />
      ) : (
        <>
          <MiniKpiStrip
            summary={bg.summary}
            max={6}
            formatValue={(k, v) => {
              const kl = String(k).toLowerCase();
              if (kl.includes('value')) return formatKpiAdaptiveINR(Number(v));
              return formatCountValue(v);
            }}
          />
          <ProjectSummaryTable
            meta="Project-wise summary"
            emptyText="No project-wise rows for the selected reporting period."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'bgCount', label: 'BG count', align: 'right' },
              { key: 'bgValue', label: 'BG value (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
              { key: 'insuranceCount', label: 'Insurance count', align: 'right' },
            ]}
            rows={bg.byProject || []}
          />
        </>
      )}

      <SectionHeader id="sm-ms" slug="Module 4" title="Milestones / EOT" />
      {msEmpty ? (
        <EmptyStateCard />
      ) : (
        <>
          <MiniKpiStrip summary={ms.summary} max={6} />
          <ProjectSummaryTable
            meta="Project-wise summary"
            emptyText="No project-wise rows for the selected reporting period."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'totalMilestones', label: 'Milestones', align: 'right' },
              { key: 'achieved', label: 'Achieved', align: 'right' },
              { key: 'pending', label: 'Pending', align: 'right' },
              { key: 'eotRequests', label: 'EOT requests', align: 'right' },
            ]}
            rows={ms.byProject || []}
          />
        </>
      )}

      <SectionHeader id="sm-dwg" slug="Module 5" title="Drawings & design" />
      {dwEmpty ? (
        <EmptyStateCard />
      ) : (
        <>
          <MiniKpiStrip summary={dw.summary} max={6} />
          <ProjectSummaryTable
            meta="Project-wise summary"
            emptyText="No project-wise rows for the selected reporting period."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'drawingCount', label: 'Total', align: 'right' },
              { key: 'submitted', label: 'Submitted', align: 'right' },
              { key: 'approved', label: 'Approved', align: 'right' },
              { key: 'underReview', label: 'Under review', align: 'right' },
              { key: 'revised', label: 'Revised', align: 'right' },
            ]}
            rows={dw.byProject || []}
          />
        </>
      )}

      <SectionHeader id="sm-dev" slug="Module 6" title="Deviations / extra / specialized" />
      {devEmpty ? (
        <EmptyStateCard />
      ) : (
        <>
          <MiniKpiStrip
            summary={dev.summary}
            max={6}
            formatValue={(k, v) => {
              const kl = String(k).toLowerCase();
              if (kl.includes('amount')) return fmtInr(v);
              return formatCountValue(v);
            }}
          />
          <ProjectSummaryTable
            meta="Project-wise summary"
            emptyText="No project-wise rows for the selected reporting period."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'recordCount', label: 'Records', align: 'right' },
              { key: 'amount', label: 'Amount', align: 'right', render: (v) => fmtInr(v) },
              { key: 'approved', label: 'Approved', align: 'right' },
              { key: 'pending', label: 'Pending', align: 'right' },
              { key: 'rejected', label: 'Rejected', align: 'right' },
            ]}
            rows={dev.byProject || []}
          />
        </>
      )}

      <SectionHeader id="sm-qaqc" slug="Module 7" title="QAQC" />
      {qaqcEmpty ? (
        <EmptyStateCard />
      ) : (
        <>
          <MiniKpiStrip summary={qaqc.summary} max={6} />
          <ProjectSummaryTable
            meta="Project-wise summary"
            emptyText="No project-wise rows for the selected reporting period."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'total', label: 'Total', align: 'right' },
              { key: 'approved', label: 'Approved', align: 'right' },
              { key: 'rejected', label: 'Rejected', align: 'right' },
              { key: 'closed', label: 'Closed', align: 'right' },
              { key: 'pending', label: 'Pending', align: 'right' },
            ]}
            rows={qaqc.byProject || []}
          />
        </>
      )}

      <SectionHeader id="sm-clr" slug="Module 8" title="Clearance" />
      {clrEmpty ? (
        <EmptyStateCard />
      ) : (
        <>
          <MiniKpiStrip summary={clr.summary} max={6} />
          <ProjectSummaryTable
            meta="Project-wise summary"
            emptyText="No project-wise rows for the selected reporting period."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'total', label: 'Total', align: 'right' },
              { key: 'internal', label: 'Internal', align: 'right' },
              { key: 'external', label: 'External', align: 'right' },
              { key: 'approved', label: 'Approved', align: 'right' },
              { key: 'pending', label: 'Pending', align: 'right' },
              { key: 'closed', label: 'Closed', align: 'right' },
            ]}
            rows={clr.byProject || []}
          />
        </>
      )}

      <SectionHeader id="sm-scope" slug="Module 9" title="Agreement & scope" />
      {scopeEmpty ? (
        <EmptyStateCard />
      ) : (
        <>
          <MiniKpiStrip
            summary={scope.summary}
            max={6}
            formatValue={(k, v) => {
              const kl = String(k).toLowerCase();
              if (kl.includes('value')) return formatKpiAdaptiveINR(Number(v));
              return formatCountValue(v);
            }}
          />
          <ProjectSummaryTable
            meta="Project-wise summary"
            emptyText="No project-wise rows for the selected reporting period."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'agreementValue', label: 'Agreement value (₹)', align: 'right', render: (v) => (v != null ? fmtInrNoSymbol(v) : '—') },
              { key: 'scopeReference', label: 'Scope reference' },
              { key: 'scopeDate', label: 'Scope date' },
              { key: 'handoverDate', label: 'Handover date' },
              { key: 'siteOfficeCount', label: 'Site offices', align: 'right' },
              { key: 'clientOfficeCount', label: 'Client offices', align: 'right' },
            ]}
            rows={scope.byProject || []}
          />
        </>
      )}

      <SectionHeader id="sm-comp" slug="Module 10" title="Period comparison" />
      <p className="sm-toolbar-note sm-comp-note">
        Previous period: {meta?.previousPeriodFrom} → {meta?.previousPeriodTo} (duration matched to current period).
      </p>
      <ProjectSummaryTable
        meta="Period-over-period comparison"
        emptyText="No comparison metrics for the selected periods."
        columns={[
          { key: 'metric', label: 'Metric' },
          { key: 'current', label: 'Current period' },
          { key: 'previous', label: 'Previous period' },
        ]}
        rows={comparisonRows}
      />

      {/* Annexures — detailed registers */}
      <section id="sm-annex" className="sm-annex">
        <h2 className="sm-annex__title">Annexures</h2>
        <p className="sm-annex__lead">Detailed line-item registers for the selected scope and period.</p>

        {!corrEmpty && (corr.details || []).length > 0 ? (
          <Annex title="Annex A — Correspondence register (period entries)" recordCount={(corr.details || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'chainId', label: 'Subject ID', align: 'right' },
                { key: 'entryId', label: 'Entry ID', align: 'right' },
                { key: 'projectName', label: 'Project' },
                { key: 'subject', label: 'Correspondence Subject' },
                { key: 'direction', label: 'Direction' },
                { key: 'letterDate', label: 'Letter date' },
                { key: 'type', label: 'Type' },
                { key: 'status', label: 'Subject status' },
                { key: 'fromName', label: 'From' },
                { key: 'toName', label: 'To' },
                { key: 'referenceNo', label: 'Reference' },
                { key: 'attachmentPresent', label: 'Attachment' },
              ]}
              rows={corr.details || []}
            />
          </Annex>
        ) : null}

        {!raEmpty && (ra.details || []).length > 0 ? (
          <Annex title="Annex B — RA bill register" recordCount={(ra.details || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'projectName', label: 'Project' },
                { key: 'raBillNo', label: 'Bill no.' },
                { key: 'billDate', label: 'Record date' },
                { key: 'periodFrom', label: 'Period from' },
                { key: 'periodTo', label: 'Period to' },
                { key: 'grossWorkDone', label: 'Gross (CPWD Σ) (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
                { key: 'netWorkDone', label: 'Net (CPWD) (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
                { key: 'deductions', label: 'Deductions (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
                { key: 'recoveries', label: 'Recoveries (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
                { key: 'certifiedValue', label: 'Certified (₹)', align: 'right', render: (v) => (v != null ? fmtInrNoSymbol(v) : '—') },
                { key: 'receivedValue', label: 'Received (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
                { key: 'pendingValue', label: 'Pending (₹)', align: 'right', render: (v) => fmtInrNoSymbol(v) },
                { key: 'status', label: 'Status' },
                { key: 'paymentStatus', label: 'Payment status' },
              ]}
              rows={ra.details || []}
            />
          </Annex>
        ) : null}

        {!bgEmpty && (bg.details || []).length > 0 ? (
          <Annex title="Annex C — Bank guarantee register" recordCount={(bg.details || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'projectName', label: 'Project' },
                { key: 'bgType', label: 'Type' },
                { key: 'bgNo', label: 'Reference' },
                { key: 'bank', label: 'Bank' },
                { key: 'amount', label: 'Amount', align: 'right', render: (v) => fmtInr(v) },
                { key: 'issueDate', label: 'Issue date' },
                { key: 'expiryDate', label: 'Expiry date' },
                { key: 'claimDate', label: 'Claim date' },
                { key: 'status', label: 'Status' },
              ]}
              rows={bg.details || []}
            />
          </Annex>
        ) : null}

        {!bgEmpty && (bg.insuranceDetails || []).length > 0 ? (
          <Annex title="Annex D — Insurance register" recordCount={(bg.insuranceDetails || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'projectName', label: 'Project' },
                { key: 'policyType', label: 'Policy type' },
                { key: 'policyNo', label: 'Policy no.' },
                { key: 'insurer', label: 'Insurer' },
                { key: 'startDate', label: 'Start' },
                { key: 'endDate', label: 'End' },
                { key: 'sumInsured', label: 'Sum insured', align: 'right', render: (v) => fmtInr(v) },
                { key: 'status', label: 'Status' },
              ]}
              rows={bg.insuranceDetails || []}
            />
          </Annex>
        ) : null}

        {!msEmpty && (ms.details || []).length > 0 ? (
          <Annex title="Annex E — Milestone register" recordCount={(ms.details || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'projectName', label: 'Project' },
                { key: 'milestoneName', label: 'Name' },
                { key: 'baselineDate', label: 'Baseline' },
                { key: 'plannedDate', label: 'Planned' },
                { key: 'actualDate', label: 'Actual' },
                { key: 'revisedDate', label: 'Revised' },
                { key: 'status', label: 'Status' },
              ]}
              rows={ms.details || []}
            />
          </Annex>
        ) : null}

        {!msEmpty && (ms.eotDetails || []).length > 0 ? (
          <Annex title="Annex F — EOT register" recordCount={(ms.eotDetails || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'projectName', label: 'Project' },
                { key: 'eotRefNo', label: 'Reference' },
                { key: 'submissionDate', label: 'Submission date' },
                { key: 'requestedExtension', label: 'Requested extension' },
                { key: 'approvedExtension', label: 'Approved extension' },
                { key: 'status', label: 'Status' },
              ]}
              rows={ms.eotDetails || []}
            />
          </Annex>
        ) : null}

        {!dwEmpty && (dw.details || []).length > 0 ? (
          <Annex title="Annex G — Drawing register" recordCount={(dw.details || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'projectName', label: 'Project' },
                { key: 'drawingNo', label: 'Drawing no.' },
                { key: 'drawingTitle', label: 'Title' },
                { key: 'discipline', label: 'Discipline' },
                { key: 'revision', label: 'Revision' },
                { key: 'submissionDate', label: 'Submission' },
                { key: 'approvalDate', label: 'Approval' },
                { key: 'currentStatus', label: 'Status' },
              ]}
              rows={dw.details || []}
            />
          </Annex>
        ) : null}

        {!devEmpty && (dev.details || []).length > 0 ? (
          <Annex title="Annex H — Deviations / extra / specialized register" recordCount={(dev.details || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'projectName', label: 'Project' },
                { key: 'recordType', label: 'Record type' },
                { key: 'refNo', label: 'Reference' },
                { key: 'description', label: 'Description' },
                { key: 'date', label: 'Date' },
                { key: 'quantity', label: 'Qty', align: 'right' },
                { key: 'unit', label: 'Unit' },
                { key: 'amount', label: 'Amount', align: 'right', render: (v) => fmtInr(v) },
                { key: 'status', label: 'Status' },
              ]}
              rows={dev.details || []}
            />
          </Annex>
        ) : null}

        {!qaqcEmpty && (qaqc.details || []).length > 0 ? (
          <Annex title="Annex I — QAQC register" recordCount={(qaqc.details || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'projectName', label: 'Project' },
                { key: 'inspectionNo', label: 'Inspection' },
                { key: 'location', label: 'Location' },
                { key: 'category', label: 'Category' },
                { key: 'date', label: 'Date' },
                { key: 'status', label: 'Status' },
                { key: 'closureDate', label: 'Closure date' },
              ]}
              rows={qaqc.details || []}
            />
          </Annex>
        ) : null}

        {!clrEmpty && (clr.details || []).length > 0 ? (
          <Annex title="Annex J — Clearance register" recordCount={(clr.details || []).length}>
            <ProjectSummaryTable
              className="sm-annex-table"
              columns={[
                { key: 'projectName', label: 'Project' },
                { key: 'clearanceType', label: 'Type' },
                { key: 'referenceNo', label: 'Reference' },
                { key: 'submissionDate', label: 'Submission' },
                { key: 'approvalDate', label: 'Approval' },
                { key: 'status', label: 'Status' },
              ]}
              rows={clr.details || []}
            />
          </Annex>
        ) : null}
      </section>
    </>
  );
};

export default SeniorManagementReportContent;
